import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, FileText, ChevronDown, ChevronUp, Image as ImageIcon, Tag, Palette, Camera, Type, Share2, Search, Filter, Trash2, RefreshCw, CheckSquare, Square, Copy, FileDown } from "lucide-react";
import { toast } from "sonner";
import { AnalysisData } from "@/pages/Index";
import jsPDF from "jspdf";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";

interface BulkAnalysisResultsProps {
  results: Array<{ file: File; data: AnalysisData }>;
  onReanalyze?: (files: File[]) => Promise<void>;
  onRemove?: (indices: number[]) => void;
}

export const BulkAnalysisResults = ({ results, onReanalyze, onRemove }: BulkAnalysisResultsProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [qualityFilter, setQualityFilter] = useState<string>("all");
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const downloadAggregatedCSV = () => {
    const headers = [
      "Filename",
      "Caption",
      "Tags",
      "Colors",
      "Scene",
      "Alt Text",
      "Social Caption",
      "OCR Text",
      "Quality Score"
    ];

    const rows = results.map(({ file, data }) => [
      file.name,
      data.caption || "",
      data.tags?.map(t => `${t.label}(${t.confidence})`).join("; ") || "",
      data.colors?.join(", ") || "",
      data.scene || "",
      data.altText || "",
      data.socialCaption || "",
      data.ocrText || "",
      data.quality?.score?.toString() || ""
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bulk-analysis-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("CSV downloaded successfully");
  };

  const downloadAggregatedPDF = async () => {
    const pdf = new jsPDF();
    let yPosition = 20;

    pdf.setFontSize(18);
    pdf.text("Bulk Image Analysis Report", 20, yPosition);
    yPosition += 15;

    pdf.setFontSize(10);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, 20, yPosition);
    pdf.text(`Total Images: ${results.length}`, 20, yPosition + 5);
    yPosition += 20;

    for (let i = 0; i < results.length; i++) {
      const { file, data } = results[i];

      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(12);
      pdf.setFont(undefined, "bold");
      pdf.text(`${i + 1}. ${file.name}`, 20, yPosition);
      yPosition += 7;

      pdf.setFontSize(9);
      pdf.setFont(undefined, "normal");

      const addField = (label: string, value: string, maxWidth = 170) => {
        if (!value) return;
        const lines = pdf.splitTextToSize(`${label}: ${value}`, maxWidth);
        pdf.text(lines, 25, yPosition);
        yPosition += lines.length * 5;
      };

      addField("Caption", data.caption);
      addField("Scene", data.scene);
      addField("Alt Text", data.altText);
      addField("Tags", data.tags?.map(t => t.label).join(", ") || "");
      addField("Colors", data.colors?.join(", ") || "");
      if (data.quality) {
        addField("Quality Score", `${data.quality.score}/100`);
      }

      yPosition += 10;
    }

    pdf.save(`bulk-analysis-report-${Date.now()}.pdf`);
    toast.success("PDF report downloaded successfully");
  };

  if (results.length === 0) {
    return null;
  }

  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const toggleItem = (index: string) => {
    setExpandedItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const toggleSelection = (index: number) => {
    setSelectedIndices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIndices.size === filteredResults.length) {
      setSelectedIndices(new Set());
    } else {
      setSelectedIndices(new Set(filteredResults.map((_, idx) => results.indexOf(_))));
    }
  };

  // Filter results based on search and quality
  const filteredResults = results.filter(({ file, data }) => {
    // Search filter
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || 
      file.name.toLowerCase().includes(searchLower) ||
      data.caption?.toLowerCase().includes(searchLower) ||
      data.tags?.some(tag => tag.label.toLowerCase().includes(searchLower));

    // Quality filter
    const matchesQuality = qualityFilter === "all" ||
      (qualityFilter === "high" && data.quality && data.quality.score >= 80) ||
      (qualityFilter === "medium" && data.quality && data.quality.score >= 50 && data.quality.score < 80) ||
      (qualityFilter === "low" && data.quality && data.quality.score < 50);

    return matchesSearch && matchesQuality;
  });

  const shareIndividual = async (file: File, data: AnalysisData) => {
    try {
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve) => {
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
      
      const imageUrl = await base64Promise;

      const { data: shareData, error } = await supabase.functions.invoke("share-analysis", {
        body: { analysisData: data, imageUrl },
      });

      if (error) throw error;

      const shareUrl = `${window.location.origin}/?share=${shareData.shareToken}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied to clipboard!");
    } catch (error: any) {
      console.error("Error sharing:", error);
      toast.error("Failed to create share link");
    }
  };

  const downloadIndividualPDF = (file: File, data: AnalysisData) => {
    const pdf = new jsPDF();
    let yPosition = 20;

    pdf.setFontSize(18);
    pdf.text("Image Analysis Report", 20, yPosition);
    yPosition += 15;

    pdf.setFontSize(10);
    pdf.text(`File: ${file.name}`, 20, yPosition);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, 20, yPosition + 5);
    yPosition += 20;

    const addField = (label: string, value: string, maxWidth = 170) => {
      if (!value) return;
      pdf.setFontSize(9);
      const lines = pdf.splitTextToSize(`${label}: ${value}`, maxWidth);
      pdf.text(lines, 20, yPosition);
      yPosition += lines.length * 5;
    };

    addField("Caption", data.caption);
    addField("Scene", data.scene);
    addField("Alt Text", data.altText);
    addField("Tags", data.tags?.map(t => t.label).join(", ") || "");
    addField("Colors", data.colors?.join(", ") || "");
    if (data.quality) {
      addField("Quality Score", `${data.quality.score}/100`);
    }

    pdf.save(`analysis-${file.name.replace(/\.[^/.]+$/, "")}-${Date.now()}.pdf`);
    toast.success("PDF downloaded successfully");
  };

  const downloadIndividualCSV = (file: File, data: AnalysisData) => {
    const headers = ["Field", "Value"];
    const rows = [
      ["Filename", file.name],
      ["Caption", data.caption || ""],
      ["Tags", data.tags?.map(t => `${t.label}(${t.confidence})`).join("; ") || ""],
      ["Colors", data.colors?.join(", ") || ""],
      ["Scene", data.scene || ""],
      ["Alt Text", data.altText || ""],
      ["Social Caption", data.socialCaption || ""],
      ["OCR Text", data.ocrText || ""],
      ["Quality Score", data.quality?.score?.toString() || ""]
    ];

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `analysis-${file.name.replace(/\.[^/.]+$/, "")}-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("CSV downloaded successfully");
  };

  const handleBatchDelete = () => {
    if (selectedIndices.size === 0) {
      toast.error("No items selected");
      return;
    }
    
    if (onRemove) {
      onRemove(Array.from(selectedIndices));
      setSelectedIndices(new Set());
      toast.success(`Deleted ${selectedIndices.size} item(s)`);
    }
  };

  const handleBatchReanalyze = async () => {
    if (selectedIndices.size === 0) {
      toast.error("No items selected");
      return;
    }

    if (onReanalyze) {
      setIsReanalyzing(true);
      const selectedFiles = Array.from(selectedIndices).map(idx => results[idx].file);
      try {
        await onReanalyze(selectedFiles);
        setSelectedIndices(new Set());
      } catch (error) {
        console.error("Reanalyze error:", error);
      } finally {
        setIsReanalyzing(false);
      }
    }
  };

  const handleBatchExport = () => {
    if (selectedIndices.size === 0) {
      toast.error("No items selected");
      return;
    }

    const selectedResults = Array.from(selectedIndices).map(idx => results[idx]);
    
    const headers = ["Filename", "Caption", "Tags", "Colors", "Scene", "Alt Text", "Social Caption", "OCR Text", "Quality Score"];
    const rows = selectedResults.map(({ file, data }) => [
      file.name,
      data.caption || "",
      data.tags?.map(t => `${t.label}(${t.confidence})`).join("; ") || "",
      data.colors?.join(", ") || "",
      data.scene || "",
      data.altText || "",
      data.socialCaption || "",
      data.ocrText || "",
      data.quality?.score?.toString() || ""
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `selected-analysis-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${selectedIndices.size} item(s)`);
  };

  return (
    <Card className="p-4 sm:p-6">
      <CardHeader className="px-0 pt-0">
        <CardTitle className="text-xl sm:text-2xl">Analysis Results</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          {results.length} image(s) analyzed • {selectedIndices.size} selected
        </p>
      </CardHeader>

      <CardContent className="px-0 pb-0 space-y-4">
        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by filename, caption, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={qualityFilter} onValueChange={setQualityFilter}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Quality filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Quality</SelectItem>
              <SelectItem value="high">High (80+)</SelectItem>
              <SelectItem value="medium">Medium (50-79)</SelectItem>
              <SelectItem value="low">Low (&lt;50)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Batch Actions */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleSelectAll}
            className="h-9"
          >
            {selectedIndices.size === filteredResults.length ? (
              <CheckSquare className="mr-2 h-4 w-4" />
            ) : (
              <Square className="mr-2 h-4 w-4" />
            )}
            {selectedIndices.size === filteredResults.length ? "Deselect All" : "Select All"}
          </Button>
          
          {selectedIndices.size > 0 && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBatchExport}
                className="h-9"
              >
                <FileDown className="mr-2 h-4 w-4" />
                Export Selected
              </Button>
              {onReanalyze && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBatchReanalyze}
                  disabled={isReanalyzing}
                  className="h-9"
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${isReanalyzing ? 'animate-spin' : ''}`} />
                  Re-analyze
                </Button>
              )}
              {onRemove && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBatchDelete}
                  className="h-9"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              )}
            </>
          )}
        </div>

        {/* Aggregate Export Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-border">
          <Button
            onClick={downloadAggregatedCSV}
            variant="outline"
            className="flex-1 h-11"
          >
            <Download className="mr-2 h-4 w-4" />
            Download All CSV
          </Button>
          <Button
            onClick={downloadAggregatedPDF}
            variant="outline"
            className="flex-1 h-11"
          >
            <FileText className="mr-2 h-4 w-4" />
            Download All PDF
          </Button>
        </div>

        <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
          {filteredResults.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Search className="mx-auto h-12 w-12 mb-4 opacity-50" />
              <p>No results found</p>
              <p className="text-sm mt-1">Try adjusting your search or filters</p>
            </div>
          ) : (
            filteredResults.map(({ file, data }, filteredIndex) => {
              const originalIndex = results.findIndex(r => r.file === file);
              const imageUrl = URL.createObjectURL(file);
              const itemId = `item-${originalIndex}`;
              const isExpanded = expandedItems.includes(itemId);
              const isSelected = selectedIndices.has(originalIndex);

              return (
                <Card key={originalIndex} className={`overflow-hidden transition-colors ${isSelected ? 'ring-2 ring-primary' : ''}`}>
                  <div className="p-4 space-y-3">
                    {/* Header with checkbox, image thumbnail and basic info */}
                    <div className="flex gap-4">
                      <div className="flex items-start pt-1">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelection(originalIndex)}
                        />
                      </div>
                      <div className="flex-shrink-0">
                        <img
                          src={imageUrl}
                          alt={file.name}
                          className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg border border-border"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm sm:text-base text-foreground break-words">
                          {file.name}
                        </h3>
                        {data.quality && (
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="secondary" className="text-xs">
                              Quality: {data.quality.score}/100
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {data.quality.sharpness}
                            </Badge>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Caption - always visible */}
                    <div className="space-y-1">
                      <div className="flex items-start gap-2">
                        <Type className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-muted-foreground">Caption</p>
                          <p className="text-sm text-foreground mt-1">{data.caption}</p>
                        </div>
                      </div>
                    </div>

                    {/* Individual Actions */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => shareIndividual(file, data)}
                        className="h-8 text-xs"
                      >
                        <Share2 className="mr-1.5 h-3.5 w-3.5" />
                        Share
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadIndividualCSV(file, data)}
                        className="h-8 text-xs"
                      >
                        <Download className="mr-1.5 h-3.5 w-3.5" />
                        CSV
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadIndividualPDF(file, data)}
                        className="h-8 text-xs"
                      >
                        <FileText className="mr-1.5 h-3.5 w-3.5" />
                        PDF
                      </Button>
                    </div>

                    {/* Expand/Collapse button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleItem(itemId)}
                      className="w-full justify-between h-8"
                    >
                      <span className="text-xs">
                        {isExpanded ? "Show less" : "Show more details"}
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>

                    {/* Expandable detailed content */}
                    {isExpanded && (
                    <div className="space-y-4 pt-2 border-t border-border">
                      {/* Scene Description */}
                      {data.scene && (
                        <div className="flex items-start gap-2">
                          <ImageIcon className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-muted-foreground">Scene Description</p>
                            <p className="text-sm text-foreground mt-1">{data.scene}</p>
                          </div>
                        </div>
                      )}

                      {/* Tags */}
                      {data.tags && data.tags.length > 0 && (
                        <div className="flex items-start gap-2">
                          <Tag className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-medium text-muted-foreground mb-2">Tags</p>
                            <div className="flex flex-wrap gap-1">
                              {data.tags.map((tag, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  {tag.label} ({Math.round(tag.confidence * 100)}%)
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Colors */}
                      {data.colors && data.colors.length > 0 && (
                        <div className="flex items-start gap-2">
                          <Palette className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-medium text-muted-foreground mb-2">Color Palette</p>
                            <div className="flex flex-wrap gap-2">
                              {data.colors.map((color, i) => (
                                <div key={i} className="flex items-center gap-1">
                                  <div
                                    className="w-6 h-6 rounded border border-border"
                                    style={{ backgroundColor: color }}
                                  />
                                  <span className="text-xs text-muted-foreground">{color}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Alt Text */}
                      {data.altText && (
                        <div className="flex items-start gap-2">
                          <Camera className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-muted-foreground">Alt Text</p>
                            <p className="text-sm text-foreground mt-1">{data.altText}</p>
                          </div>
                        </div>
                      )}

                      {/* Social Caption */}
                      {data.socialCaption && (
                        <div className="flex items-start gap-2">
                          <Share2 className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-muted-foreground">Social Media Caption</p>
                            <p className="text-sm text-foreground mt-1">{data.socialCaption}</p>
                          </div>
                        </div>
                      )}

                      {/* OCR Text */}
                      {data.ocrText && (
                        <div className="flex items-start gap-2">
                          <Type className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-muted-foreground">Text Found in Image</p>
                            <p className="text-sm text-foreground mt-1 font-mono bg-muted p-2 rounded text-xs whitespace-pre-wrap">
                              {data.ocrText}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* EXIF Data */}
                      {data.exif && (
                        <div className="flex items-start gap-2">
                          <Camera className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-medium text-muted-foreground mb-2">Camera Info</p>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              {data.exif.make && (
                                <div>
                                  <span className="text-muted-foreground">Make:</span>
                                  <span className="ml-1 text-foreground">{data.exif.make}</span>
                                </div>
                              )}
                              {data.exif.model && (
                                <div>
                                  <span className="text-muted-foreground">Model:</span>
                                  <span className="ml-1 text-foreground">{data.exif.model}</span>
                                </div>
                              )}
                              {data.exif.iso && (
                                <div>
                                  <span className="text-muted-foreground">ISO:</span>
                                  <span className="ml-1 text-foreground">{data.exif.iso}</span>
                                </div>
                              )}
                              {data.exif.fNumber && (
                                <div>
                                  <span className="text-muted-foreground">Aperture:</span>
                                  <span className="ml-1 text-foreground">f/{data.exif.fNumber}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                </Card>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
};